/** @jsx jsx */

import { Fragment, useState } from 'react';

import { Spacer, Text, ToggleSwitch } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import Divider from 'app/developer-v3/components/Divider';
import EditorBodyLayout from 'app/developer-v3/layouts/EditorBodyLayout';
import Link from 'app/common/components/Link';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import { useCurrentVersionReadOnlyReason } from 'app/developer-v3/hooks';

const Styles = {
  ips: css`
    list-style-type: disc;
    margin-left: 25px;
  `,
  toggleWrapper: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
  `,
  toggleTitle: css`
    font-weight: 600;
  `,
  toggleText: css`
    margin: 0;
    padding: 0;
  `,
};

type Props = {
  isGUIIntegration: boolean;
  isIntegrationMaintenanceToggleVisible: boolean;
  isInitialSkipHttpPatch: boolean;
  isInitialSkipThrowForStatus: boolean;
  isInitialStaticIPEnabled: boolean;
  isIntegrationPrivate: boolean;
  isSaving: boolean;
  onSave: ({
    isSkipHttpPatch,
    isSkipThrowForStatus,
    isStaticIPEnabled,
  }: {
    isSkipHttpPatch: boolean;
    isSkipThrowForStatus: boolean;
    isStaticIPEnabled: boolean;
  }) => Promise<void>;
};

const defaultProps = {
  initialStaticIPEnabled: false,
  initialSkipHttpPatch: false,
  initialSkipThrowForStatus: false,
};

function Settings({
  isGUIIntegration,
  isIntegrationMaintenanceToggleVisible,
  isInitialSkipHttpPatch,
  isInitialSkipThrowForStatus,
  isInitialStaticIPEnabled,
  isIntegrationPrivate,
  isSaving,
  onSave,
}: Props) {
  const [skipHttpPatch, setSkipHttpPatch] = useState(isInitialSkipHttpPatch);
  const [skipThrowForStatus, setSkipThrowForStatus] = useState(
    isInitialSkipThrowForStatus
  );
  const [isStaticIPEnabled, setIsStaticIPEnabled] = useState(
    isInitialStaticIPEnabled
  );

  const isReadOnly = !!useCurrentVersionReadOnlyReason();

  return (
    <EditorBodyLayout
      isDisabled={isReadOnly}
      isSaving={isSaving}
      onSave={async () =>
        onSave({
          isSkipHttpPatch: skipHttpPatch,
          isSkipThrowForStatus: skipThrowForStatus,
          isStaticIPEnabled,
        })
      }
    >
      <div css={Styles.toggleWrapper} data-testid="static-ip-toggle">
        <div>
          <Text margin="0 8px 0 0" type="Body4">
            Static IP
          </Text>
          <Text margin="10px 0px 0px" tag="p">
            Ensure that Zapier's traffic comes from a known, discrete number of
            IPs. With Static IP enabled, traffic comes from the following
            addresses:
          </Text>
          <Spacer height={16} />
          <ul css={Styles.ips}>
            <li>
              <Text tag="p">44.214.195.64/28 (us-east-1)</Text>
            </li>
            <li>
              <Text tag="p">18.246.81.208/28 (us-west-2)</Text>
            </li>
          </ul>
          <Text margin="10px 0px 0px" tag="p">
            You can only enable static IPs yourself when your integration is
            private. Contact Zapier support if your integration has been
            published.{' '}
            <Link href={`${BASE_DOC_URL_VB}/build/static-ip`} target="_blank">
              Learn more
            </Link>
            .
          </Text>
        </div>
        <ToggleSwitch
          checked={isStaticIPEnabled}
          disabled={isReadOnly || isSaving || !isIntegrationPrivate}
          onChange={() => setIsStaticIPEnabled(!isStaticIPEnabled)}
          // The tooltip is still "Disabled", even when the toggle is enabled
          // and read-only. So we hide it entirely.
          showDisabledTooltip={false}
        />
      </div>
      <Spacer height={32} />
      <Divider />
      <Spacer height={32} />
      <div css={Styles.toggleWrapper} data-testid="throw-for-status-toggle">
        <div>
          <Text margin="0 8px 0 0" type="Body4">
            Error response handling
          </Text>
          <Text>
            <code>skipThrowForStatus</code>
          </Text>
          <Text margin="10px 0px 0px" tag="p">
            Useful if your API returns responses with status codes above 400
            that should not automatically throw an error. Note that 401 status
            codes will throw a RefreshAuthError regardless. For more on error
            response handling, see{' '}
            <Link href={`${BASE_DOC_URL_VB}/build/errors`} target="_blank">
              here
            </Link>
            .
          </Text>
          <Text margin="10px 0px 0px" tag="p">
            The toggle is only for UI integrations. Enable it in the source
            definition for CLI integrations.
          </Text>
        </div>
        <ToggleSwitch
          checked={skipThrowForStatus}
          disabled={isReadOnly || isSaving || !isGUIIntegration}
          onChange={() => setSkipThrowForStatus(!skipThrowForStatus)}
          showDisabledTooltip={false}
        />
      </div>
      <Spacer height={32} />
      <Divider />
      <Spacer height={32} />
      <div css={Styles.toggleWrapper} data-testid="http-patch-toggle">
        <div>
          <Text margin="0 8px 0 0" type="Body4">
            HTTP module patching
          </Text>
          <Text>
            <code>skipHttpPatch</code>
          </Text>
          <Text margin="10px 0px 0px" tag="p">
            By default, Zapier patches the core <code>http</code> module so that
            all requests (including those from 3rd-party SDKs) can be logged.
            Enable this if you're seeing issues using an SDK (such as AWS).
          </Text>
          <Text margin="10px 0px 0px" tag="p">
            The toggle is only for UI integrations. Enable it in the source
            definition for CLI integrations.
          </Text>
        </div>
        <ToggleSwitch
          checked={skipHttpPatch}
          disabled={isReadOnly || isSaving || !isGUIIntegration}
          onChange={() => setSkipHttpPatch(!skipHttpPatch)}
          showDisabledTooltip={false}
        />
      </div>
      <Spacer height={32} />
      <Divider />
      {isIntegrationMaintenanceToggleVisible && (
        <Fragment>
          <Spacer height={32} />
          <div
            css={Styles.toggleWrapper}
            data-testid="maintenance-opt-in-toggle"
          >
            <div>
              <Text margin="0 8px 0 0" type="Body4">
                Zapier integration maintenance
              </Text>
            </div>
          </div>
          <Spacer height={32} />
          <Divider />
        </Fragment>
      )}
    </EditorBodyLayout>
  );
}

Settings.defaultProps = defaultProps;

export default Settings;
